import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Document, Page } from 'react-pdf';
import { Spin } from 'antd';
import { ZoomIn, ZoomOut, Crosshair, Hand, X, Moon, Sun, Eye, EyeOff, ExternalLink } from 'lucide-react';
import { HighlightRect } from '../../../sdk/@types/Acertei';
import AuthService from '../../auth/Authorization.service';

const BASE_URL = process.env.REACT_APP_AUTH_SERVER_BASE_URL ?? '';

interface OtherMark {
  page: number;
  rect: HighlightRect;
}

interface PdfViewerProps {
  fileKey: string;
  page: number;
  rect: HighlightRect;
  reviewed: boolean;
  onHide?: () => void;
  otherMarks?: OtherMark[];
}

export default function PdfViewer({ fileKey, page, rect, reviewed, onHide, otherMarks = [] }: PdfViewerProps) {
  const [numPages, setNumPages] = useState(0);
  const [pdfLoaded, setPdfLoaded] = useState(false);
  const [baseWidth, setBaseWidth] = useState(400);
  const [zoom, setZoom] = useState(1);
  const [panMode, setPanMode] = useState(false);
  const [isPanning, setIsPanning] = useState(false);
  const [darkMode, setDarkMode] = useState(false);
  const [showMarks, setShowMarks] = useState(true);
  const [opening, setOpening] = useState(false);

  const pageRefs = useRef<(HTMLDivElement | null)[]>([]);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const panStart = useRef<{ x: number; y: number; scrollTop: number; scrollLeft: number } | null>(null);

  const pageWidth = Math.round(baseWidth * zoom);

  useEffect(() => {
    const update = () => {
      if (containerRef.current) {
        setBaseWidth(Math.max(280, containerRef.current.offsetWidth - 16));
      }
    };
    update();
    window.addEventListener('resize', update);
    return () => window.removeEventListener('resize', update);
  }, []);

  useEffect(() => {
    if (!pdfLoaded) return;
    const timer = setTimeout(() => {
      pageRefs.current[page - 1]?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 400);
    return () => clearTimeout(timer);
  }, [page, pdfLoaded]);

  const openInNewTab = async () => {
    setOpening(true);
    try {
      const res = await fetch(`${BASE_URL}/files/${fileKey}`, {
        headers: { Authorization: `Bearer ${AuthService.getAccessToken()}` },
      });
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = window.open(url, '_blank');
      if (a) setTimeout(() => URL.revokeObjectURL(url), 60000);
    } finally {
      setOpening(false);
    }
  };

  const scrollToHighlight = () => {
    pageRefs.current[page - 1]?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const handleMouseDown = (e: React.MouseEvent) => {
    if (!panMode || !containerRef.current) return;
    e.preventDefault();
    setIsPanning(true);
    panStart.current = {
      x: e.clientX,
      y: e.clientY,
      scrollTop: containerRef.current.scrollTop,
      scrollLeft: containerRef.current.scrollLeft,
    };
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!isPanning || !panStart.current || !containerRef.current) return;
    containerRef.current.scrollTop = panStart.current.scrollTop - (e.clientY - panStart.current.y);
    containerRef.current.scrollLeft = panStart.current.scrollLeft - (e.clientX - panStart.current.x);
  };

  const stopPan = () => {
    setIsPanning(false);
    panStart.current = null;
  };

  const file = useMemo(
    () => ({
      url: `${BASE_URL}/files/${fileKey}`,
      httpHeaders: { Authorization: `Bearer ${AuthService.getAccessToken()}` },
    }),
    [fileKey]
  );

  return (
    <div className="relative h-full flex flex-col bg-gray-100 dark:bg-[#0a0f0d]">
      {/* Toolbar */}
      <div className="flex items-center gap-1 px-3 py-1.5 bg-white dark:bg-[#0f1a10] border-b border-gray-200 dark:border-[#1a2e1c] flex-shrink-0">
        {/* Zoom */}
        <button
          onClick={() => setZoom(z => Math.max(0.25, +(z - 0.25).toFixed(2)))}
          className="p-1 rounded text-gray-500 dark:text-[#6b8f6e] hover:text-[#a3e635] hover:bg-gray-100 dark:hover:bg-[#1a2e1c] transition-colors"
          title="Diminuir zoom"
        >
          <ZoomOut size={14} />
        </button>
        <span className="text-xs text-gray-500 dark:text-[#6b8f6e] w-10 text-center select-none">
          {Math.round(zoom * 100)}%
        </span>
        <button
          onClick={() => setZoom(z => Math.min(3, +(z + 0.25).toFixed(2)))}
          className="p-1 rounded text-gray-500 dark:text-[#6b8f6e] hover:text-[#a3e635] hover:bg-gray-100 dark:hover:bg-[#1a2e1c] transition-colors"
          title="Aumentar zoom"
        >
          <ZoomIn size={14} />
        </button>

        <div className="w-px h-4 bg-gray-200 dark:bg-[#1a2e1c] mx-1" />

        {/* Go to highlight */}
        <button
          onClick={scrollToHighlight}
          className="flex items-center gap-1 px-2 py-0.5 rounded text-xs text-gray-500 dark:text-[#6b8f6e] hover:text-[#a3e635] hover:bg-gray-100 dark:hover:bg-[#1a2e1c] transition-colors"
          title="Ir para o trecho destacado"
        >
          <Crosshair size={12} />
          <span>Ir para trecho</span>
        </button>

        <div className="w-px h-4 bg-gray-200 dark:bg-[#1a2e1c] mx-1" />

        {/* Pan mode toggle */}
        <button
          onClick={() => setPanMode(m => !m)}
          className={`p-1 rounded transition-colors ${
            panMode
              ? 'text-[#a3e635] bg-[#a3e635]/10 border border-[#a3e635]/30'
              : 'text-gray-500 dark:text-[#6b8f6e] hover:text-[#a3e635] hover:bg-gray-100 dark:hover:bg-[#1a2e1c]'
          }`}
          title={panMode ? 'Desativar arrastar' : 'Arrastar PDF'}
        >
          <Hand size={14} />
        </button>

        {/* Dark mode toggle */}
        <button
          onClick={() => setDarkMode(m => !m)}
          className={`p-1 rounded transition-colors ${
            darkMode
              ? 'text-[#a3e635] bg-[#a3e635]/10 border border-[#a3e635]/30'
              : 'text-gray-500 dark:text-[#6b8f6e] hover:text-[#a3e635] hover:bg-gray-100 dark:hover:bg-[#1a2e1c]'
          }`}
          title={darkMode ? 'Modo claro' : 'Modo escuro'}
        >
          {darkMode ? <Sun size={14} /> : <Moon size={14} />}
        </button>

        {/* Show/hide marks */}
        <button
          onClick={() => setShowMarks(m => !m)}
          className={`p-1 rounded transition-colors ${
            showMarks
              ? 'text-[#a3e635] bg-[#a3e635]/10 border border-[#a3e635]/30'
              : 'text-gray-500 dark:text-[#6b8f6e] hover:text-[#a3e635] hover:bg-gray-100 dark:hover:bg-[#1a2e1c]'
          }`}
          title={showMarks ? 'Ocultar marcação' : 'Mostrar marcação'}
        >
          {showMarks ? <Eye size={14} /> : <EyeOff size={14} />}
        </button>

        {/* Open in new tab */}
        <button
          onClick={openInNewTab}
          disabled={opening}
          className="p-1 rounded text-[#6b8f6e] hover:text-[#a3e635] hover:bg-[#1a2e1c] transition-colors disabled:opacity-40"
          title="Abrir PDF em nova aba"
        >
          <ExternalLink size={14} />
        </button>

        {/* Hide */}
        {onHide && (
          <>
            <div className="w-px h-4 bg-gray-200 dark:bg-[#1a2e1c] mx-1" />
            <button
              onClick={onHide}
              className="p-1 rounded text-gray-500 dark:text-[#6b8f6e] hover:text-red-400 hover:bg-gray-100 dark:hover:bg-[#1a2e1c] transition-colors"
              title="Ocultar PDF"
            >
              <X size={14} />
            </button>
          </>
        )}

        {!reviewed && (
          <span className="ml-auto text-xs text-orange-400 flex-shrink-0">⚠ não revisado</span>
        )}
      </div>

      {/* Scrollable PDF area */}
      <div
        ref={containerRef}
        className="flex-1 overflow-auto"
        style={{
          cursor: panMode ? (isPanning ? 'grabbing' : 'grab') : 'default',
          userSelect: isPanning ? 'none' : 'auto',
        }}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={stopPan}
        onMouseLeave={stopPan}
      >
        <Document
          file={file}
          onLoadSuccess={({ numPages: n }) => {
            setNumPages(n);
            pageRefs.current = new Array(n).fill(null);
            setPdfLoaded(true);
          }}
          loading={
            <div className="flex justify-center p-8">
              <Spin />
            </div>
          }
        >
          <div style={darkMode ? { filter: 'invert(1) hue-rotate(180deg)' } : undefined}>
          {Array.from({ length: numPages }, (_, i) => (
            <div
              key={i}
              ref={el => { pageRefs.current[i] = el; }}
              className="relative mb-2"
              style={{ width: pageWidth }}
            >
              <Page
                pageNumber={i + 1}
                width={pageWidth}
                renderTextLayer={false}
                renderAnnotationLayer={false}
              />
              {showMarks && otherMarks.filter(m => m.page === i + 1).map((m, idx) => (
                <div
                  key={idx}
                  className="absolute pointer-events-none"
                  style={{
                    left: `${m.rect.x}%`,
                    top: `${m.rect.y}%`,
                    width: `${m.rect.w}%`,
                    height: `${m.rect.h}%`,
                    background: 'rgba(136, 132, 216, 0.2)',
                    border: '2px solid #8884d8',
                    borderRadius: 3,
                    filter: darkMode ? 'invert(1) hue-rotate(180deg)' : undefined,
                  }}
                />
              ))}
              {showMarks && i + 1 === page && (
                <div
                  className="absolute pointer-events-none"
                  style={{
                    left: `${rect.x}%`,
                    top: `${rect.y}%`,
                    width: `${rect.w}%`,
                    height: `${rect.h}%`,
                    background: 'rgba(163, 230, 53, 0.22)',
                    border: '2px solid #a3e635',
                    borderRadius: 3,
                    filter: darkMode ? 'invert(1) hue-rotate(180deg)' : undefined,
                  }}
                />
              )}
            </div>
          ))}
          </div>
        </Document>
      </div>
    </div>
  );
}
